import log from 'loglevel';

import {
  POST_OAUTH_RETURN_TTL_MS,
  SESSION_STORAGE_KEYS,
} from '../constants/integrationConstants';

interface PostOAuthReturnPayload {
  returnPath: string;
  timestamp: number;
}

const UNSAFE_PATH_START_PATTERN = /^\/\/|^\/\\/;
const URL_SCHEME_PATTERN = /^[a-zA-Z][a-zA-Z\d+.-]*:/;

export function isSafeInternalPath(path: unknown): path is string {
  if (typeof path !== 'string' || path.length === 0) {
    return false;
  }
  if (!path.startsWith('/')) {
    return false;
  }
  if (UNSAFE_PATH_START_PATTERN.test(path)) {
    return false;
  }
  return !URL_SCHEME_PATTERN.test(path);
}

export function clearPostOAuthFlags(): void {
  sessionStorage.removeItem(SESSION_STORAGE_KEYS.PENDING_INTEGRATION_REFRESH);
  sessionStorage.removeItem(SESSION_STORAGE_KEYS.CONNECTING_PLATFORM);
}

export function savePostOAuthReturnPath(returnPath: string): void {
  const payload: PostOAuthReturnPayload = {
    returnPath,
    timestamp: Date.now(),
  };
  sessionStorage.setItem(
    SESSION_STORAGE_KEYS.POST_OAUTH_RETURN,
    JSON.stringify(payload),
  );
}

function parsePostOAuthReturnPayload(
  raw: string,
): PostOAuthReturnPayload | null {
  try {
    const parsed = JSON.parse(raw) as Partial<PostOAuthReturnPayload>;
    if (
      typeof parsed.returnPath !== 'string' ||
      typeof parsed.timestamp !== 'number'
    ) {
      return null;
    }
    return { returnPath: parsed.returnPath, timestamp: parsed.timestamp };
  } catch (error) {
    log.error('Failed to parse post-OAuth return payload:', error);
    return null;
  }
}

export function consumePostOAuthReturnPath(): string | null {
  const raw = sessionStorage.getItem(SESSION_STORAGE_KEYS.POST_OAUTH_RETURN);
  if (!raw) {
    return null;
  }
  sessionStorage.removeItem(SESSION_STORAGE_KEYS.POST_OAUTH_RETURN);

  const payload = parsePostOAuthReturnPayload(raw);
  if (!payload) {
    return null;
  }

  const isExpired = Date.now() - payload.timestamp >= POST_OAUTH_RETURN_TTL_MS;
  if (isExpired) {
    return null;
  }

  if (!isSafeInternalPath(payload.returnPath)) {
    log.error('Rejected unsafe post-OAuth return path:', payload.returnPath);
    return null;
  }

  return payload.returnPath;
}
